/**
 * Équipements et POI de l'organisation, en onglets :
 * - Équipements (points de l'organisation, publics ou privés) et POI (points d'intérêt publics de la carte
 *   principale) : listes par catégorie (Service, Plein air, Transport…), avec le type de chaque point et un
 *   filtre ; on les place et on les modifie dans la carte (palette), un clic y ouvre le point ;
 * Les plans du site sont dans la Fiche → Médias (l'ancien lien ?onglet=plans y mène).
 * Les emplacements de camping sont dans Refuges et campings (l'ancien lien ?onglet=emplacements y mène) ;
 * les relais sont devenus des POI « Abri / relais » (onglet POI ; l'ancien lien ?onglet=relais mène aux équipements).
 * L'onglet ouvert est dans l'adresse (?onglet=), pour y revenir ou le partager.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { usePOICategories } from '@web/features/map/hooks/usePOICategories'
import { fetchEditorBundle, fetchPois, type OrgFacility, type OrgPoi } from '../api'
import { poiIcon } from '../components/map/PoiPalette'
import { useOrg } from '../utils/orgContext'

const TABS = ['equipements', 'poi'] as const
type Tab = (typeof TABS)[number]
const OTHER = '_autres'

export function EquipementsPage() {
  const { t } = useTranslation('gestion')
  const { orgId } = useOrg()
  const [params, setParams] = useSearchParams()
  const tab: Tab = TABS.find((x) => x === params.get('onglet')) ?? 'equipements'
  const setTab = (next: Tab) => setParams(next === 'equipements' ? {} : { onglet: next }, { replace: true })
  if (params.get('onglet') === 'emplacements') return <Navigate to={`/o/${orgId}/lieux?onglet=emplacements`} replace />
  if (params.get('onglet') === 'plans') return <Navigate to={`/o/${orgId}/fiche#medias`} replace />

  return (
    <div className="g-page g-page--wide">
      <div className="g-page__head">
        <div>
          <h1 className="g-page__title">{t('equipements.titre')}</h1>
          <p className="g-muted">{t('equipements.intro_onglets')}</p>
        </div>
        <Link to={`/o/${orgId}/carte`} className="btn btn--secondary g-actions">
          <span className="mdi mdi-map-outline" aria-hidden /> {t('equipements.voir_carte')}
        </Link>
      </div>
      <div className="g-tabs" role="tablist">
        {TABS.map((x) => (
          <button key={x} type="button" role="tab" aria-selected={tab === x} className={`g-tab${tab === x ? ' g-tab--active' : ''}`} onClick={() => setTab(x)}>
            {t(`equipements.onglet_${x}`)}
          </button>
        ))}
      </div>
      {tab === 'equipements' && <FacilitiesList orgId={orgId} />}
      {tab === 'poi' && <PoiList orgId={orgId} />}
    </div>
  )
}

function FacilitiesList({ orgId }: { orgId: number }) {
  const { t } = useTranslation('gestion')
  const { data: bundle, isLoading } = useQuery({ queryKey: ['editorBundle', orgId], queryFn: ({ signal }) => fetchEditorBundle(orgId, signal) })
  const { data: poiCategories } = usePOICategories()
  const [category, setCategory] = useState<string | null>(null)

  // Catégorie d'un équipement : la catégorie parente de son type de POI (Service, Plein air…), sinon « Autres ».
  const groups = useMemo(() => {
    const names = new Map((poiCategories?.categories ?? []).map((c) => [c.slug, c.name]))
    const map = new Map<string, { key: string; name: string; items: OrgFacility[] }>()
    for (const f of bundle?.facilities ?? []) {
      const key = f.poi_type?.parent_slug || OTHER
      const name = key === OTHER ? t('equipements.autres') : names.get(key) ?? key
      if (!map.has(key)) map.set(key, { key, name, items: [] })
      map.get(key)!.items.push(f)
    }
    const label = (f: OrgFacility) => `${f.type_label || f.facility_type_label} ${f.name ?? ''}`
    return [...map.values()]
      .map((g) => ({ ...g, items: g.items.sort((a, b) => label(a).localeCompare(label(b), 'fr')) }))
      .sort((a, b) => (a.key === OTHER ? 1 : b.key === OTHER ? -1 : a.name.localeCompare(b.name, 'fr')))
  }, [bundle, poiCategories, t])

  if (isLoading) return <p className="g-muted">{t('commun.chargement')}</p>
  const total = groups.reduce((n, g) => n + g.items.length, 0)
  const shown = category ? groups.filter((g) => g.key === category) : groups

  return (
    <section className="g-card">
      <div className="g-equip__head">
        <p className="g-muted">{t('equipements.aide_liste')}</p>
        <Link to={`/o/${orgId}/carte?outil=equipement`} className="btn btn--primary">
          <span className="mdi mdi-map-marker-plus" aria-hidden /> {t('equipements.ajouter_carte')}
        </Link>
      </div>
      {total === 0 ? (
        <p className="g-muted">{t('equipements.aucun')}</p>
      ) : (
        <>
          <div className="g-chips" role="group" aria-label={t('equipements.categories')}>
            <button type="button" aria-pressed={category == null} className={`g-chip${category == null ? ' g-chip--active' : ''}`} onClick={() => setCategory(null)}>
              {t('equipements.toutes')} <span className="g-chip__count">{total}</span>
            </button>
            {groups.map((g) => (
              <button key={g.key} type="button" aria-pressed={category === g.key} className={`g-chip${category === g.key ? ' g-chip--active' : ''}`} onClick={() => setCategory(g.key)}>
                {g.name} <span className="g-chip__count">{g.items.length}</span>
              </button>
            ))}
          </div>
          {shown.map((g) => (
            <div key={g.key} className="g-equip__group">
              <h2 className="g-editor__section">
                {g.name} <span className="g-muted">({g.items.length})</span>
              </h2>
              <ul className="g-editor__list">
                {g.items.map((f) => (
                  <li key={f.id}>
                    <Link to={`/o/${orgId}/carte?equipement=${f.id}`} className="g-editor__item">
                      <span className="g-poi-marker g-equip__icon" style={{ background: f.poi_type?.icon_color || undefined }} aria-hidden>
                        <span className={`mdi ${poiIcon(f.poi_type)}`} />
                      </span>
                      <span className="g-editor__item-text">
                        <span>
                          {f.access === 'private' && <span className="mdi mdi-lock-outline g-editor__lock" title={t('carte.acces_private')} aria-label={t('carte.acces_private')} />}
                          {f.name || f.type_label || f.facility_type_label}
                        </span>
                        <span className="g-muted">
                          {[f.name ? f.type_label || f.facility_type_label : '', f.access === 'private' ? t('carte.acces_private') : t('carte.acces_public'), f.info_text || '']
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      <span className="g-muted g-equip__go">
                        {t('equipements.voir_carte')} <span className="mdi mdi-chevron-right" aria-hidden />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </section>
  )
}

/** POI publics de l'organisation, par catégorie, avec leur type (« Quai », « Escalade »…). */
function PoiList({ orgId }: { orgId: number }) {
  const { t } = useTranslation('gestion')
  const { data, isLoading } = useQuery({ queryKey: ['orgPois', orgId], queryFn: ({ signal }) => fetchPois(orgId, signal) })
  const { data: poiCategories } = usePOICategories()
  const [category, setCategory] = useState<string | null>(null)

  const groups = useMemo(() => {
    const names = new Map((poiCategories?.categories ?? []).map((c) => [c.slug, c.name]))
    const map = new Map<string, { key: string; name: string; items: OrgPoi[] }>()
    for (const p of data?.results ?? []) {
      const key = p.poi_type?.parent_slug || OTHER
      const name = key === OTHER ? t('equipements.autres') : names.get(key) ?? key
      if (!map.has(key)) map.set(key, { key, name, items: [] })
      map.get(key)!.items.push(p)
    }
    return [...map.values()]
      .map((g) => ({ ...g, items: g.items.sort((a, b) => a.name.localeCompare(b.name, 'fr')) }))
      .sort((a, b) => (a.key === OTHER ? 1 : b.key === OTHER ? -1 : a.name.localeCompare(b.name, 'fr')))
  }, [data, poiCategories, t])

  if (isLoading) return <p className="g-muted">{t('commun.chargement')}</p>
  const total = groups.reduce((n, g) => n + g.items.length, 0)
  const shown = category ? groups.filter((g) => g.key === category) : groups

  return (
    <section className="g-card">
      <div className="g-equip__head">
        <p className="g-muted">{t('equipements.aide_poi')}</p>
        <Link to={`/o/${orgId}/carte?outil=equipement`} className="btn btn--primary">
          <span className="mdi mdi-map-marker-plus" aria-hidden /> {t('equipements.ajouter_carte')}
        </Link>
      </div>
      {total === 0 ? (
        <p className="g-muted">{t('equipements.aucun_poi')}</p>
      ) : (
        <>
          <div className="g-chips" role="group" aria-label={t('equipements.categories')}>
            <button type="button" aria-pressed={category == null} className={`g-chip${category == null ? ' g-chip--active' : ''}`} onClick={() => setCategory(null)}>
              {t('equipements.toutes')} <span className="g-chip__count">{total}</span>
            </button>
            {groups.map((g) => (
              <button key={g.key} type="button" aria-pressed={category === g.key} className={`g-chip${category === g.key ? ' g-chip--active' : ''}`} onClick={() => setCategory(g.key)}>
                {g.name} <span className="g-chip__count">{g.items.length}</span>
              </button>
            ))}
          </div>
          {shown.map((g) => (
            <div key={g.key} className="g-equip__group">
              <h2 className="g-editor__section">
                {g.name} <span className="g-muted">({g.items.length})</span>
              </h2>
              <ul className="g-editor__list">
                {g.items.map((p) => (
                  <li key={p.id}>
                    <Link to={`/o/${orgId}/carte?poi=${p.id}`} className="g-editor__item">
                      <span className="g-poi-marker g-equip__icon" style={{ background: p.poi_type?.icon_color || undefined }} aria-hidden>
                        <span className={`mdi ${poiIcon(p.poi_type)}`} />
                      </span>
                      <span className="g-editor__item-text">
                        <span>{p.name}</span>
                        <span className="g-muted">{[p.poi_type?.name ?? t('equipements.type_inconnu'), p.osm_id ? 'OSM' : ''].filter(Boolean).join(' · ')}</span>
                      </span>
                      <span className="g-muted g-equip__go">
                        {t('equipements.voir_carte')} <span className="mdi mdi-chevron-right" aria-hidden />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </section>
  )
}
